import React, { useState, useEffect, useContext, useCallback } from 'react';
import AuthContext from '../context/AuthContext';
import {
  Box, Typography, Paper, Chip, CircularProgress, TextField,
  ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Collapse, IconButton,
  Button, Select, MenuItem, FormControl, InputLabel, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import BugReportIcon from '@mui/icons-material/BugReport';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { NAV_BG, AMBER, AMBER_DARK } from '../theme';
const SURFACE = '#163571';
const BORDER = 'rgba(245, 158, 11, 0.22)';
const BG = '#080d1a';

const TYPE_COLORS = {
  js_error: { bg: 'rgba(239,68,68,0.15)', color: '#f87171', label: 'JS Error' },
  promise:  { bg: 'rgba(251,146,60,0.15)', color: '#fb923c', label: 'Promise' },
  render:   { bg: 'rgba(168,85,247,0.15)', color: '#c084fc', label: 'Render' },
};

function StatCard({ label, value, color }) {
  return (
    <Paper sx={{
      flex: 1, minWidth: 140, p: 2.5, borderRadius: 3,
      background: SURFACE, border: `1px solid ${BORDER}`,
      textAlign: 'center',
    }}>
      <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: color || AMBER, lineHeight: 1 }}>
        {value ?? '—'}
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
    </Paper>
  );
}

function ErrorRow({ err }) {
  const [open, setOpen] = useState(false);
  const typeStyle = TYPE_COLORS[err.error_type] || TYPE_COLORS.js_error;
  const ts = new Date(err.created_at);

  return (
    <>
      <TableRow
        sx={{
          cursor: 'pointer',
          '&:hover': { background: 'rgba(245,158,11,0.04)' },
          borderBottom: open ? 'none' : undefined,
        }}
        onClick={() => setOpen(o => !o)}
      >
        <TableCell sx={{ width: 32, p: 0.5, pl: 1 }}>
          <IconButton size="small" sx={{ color: '#64748b' }}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap', color: '#94a3b8', fontSize: '0.78rem' }}>
          {ts.toLocaleDateString()} {ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </TableCell>
        <TableCell>
          <Chip
            label={typeStyle.label}
            size="small"
            sx={{ background: typeStyle.bg, color: typeStyle.color, fontWeight: 700, fontSize: '0.7rem', height: 22 }}
          />
        </TableCell>
        <TableCell sx={{ color: '#e2e8f0', fontSize: '0.85rem', maxWidth: 400 }}>
          <Typography noWrap sx={{ fontSize: '0.85rem', color: '#e2e8f0' }}>
            {err.message}
          </Typography>
        </TableCell>
        <TableCell sx={{ color: '#94a3b8', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
          {err.username || <span style={{ color: '#334155' }}>—</span>}
        </TableCell>
        <TableCell sx={{ color: '#94a3b8', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
          {[err.platform, err.os_version].filter(Boolean).join(' ') || '—'}
        </TableCell>
        <TableCell sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
          {err.app_version || '—'}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, pl: 6, background: 'rgba(15,45,94,0.4)', borderBottom: `1px solid ${BORDER}` }}>
              {err.device_model && (
                <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mb: 1 }}>
                  <PhoneAndroidIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                  {err.device_model}
                </Typography>
              )}
              {err.stack && (
                <Box sx={{ mb: err.component_stack ? 2 : 0 }}>
                  <Typography sx={{ fontSize: '0.7rem', color: '#64748b', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Stack trace
                  </Typography>
                  <Box component="pre" sx={{
                    m: 0, p: 1.5, borderRadius: 2,
                    background: 'rgba(0,0,0,0.4)',
                    color: '#94a3b8', fontSize: '0.72rem',
                    overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                    maxHeight: 240, overflowY: 'auto',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}>
                    {err.stack}
                  </Box>
                </Box>
              )}
              {err.component_stack && (
                <Box>
                  <Typography sx={{ fontSize: '0.7rem', color: '#64748b', mt: 2, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Component stack
                  </Typography>
                  <Box component="pre" sx={{
                    m: 0, p: 1.5, borderRadius: 2,
                    background: 'rgba(0,0,0,0.4)',
                    color: '#94a3b8', fontSize: '0.72rem',
                    overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                    maxHeight: 180, overflowY: 'auto',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}>
                    {err.component_stack}
                  </Box>
                </Box>
              )}
              {!err.stack && !err.component_stack && (
                <Typography sx={{ fontSize: '0.82rem', color: '#334155' }}>No stack trace available.</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function AppErrorsPage() {
  const { authTokens, backendUrl } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [days, setDays] = useState('7');
  const [errorType, setErrorType] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [clearDlg, setClearDlg] = useState(false);
  const [clearing, setClearing] = useState(false);

  const authHeader = { Authorization: 'Bearer ' + authTokens?.access };

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (days) params.set('days', days);
      if (errorType) params.set('error_type', errorType);
      if (search) params.set('search', search);
      const res = await fetch(`${backendUrl}api/app-errors/?${params}`, { headers: authHeader });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [page, days, errorType, search, authTokens, backendUrl]);

  useEffect(() => { fetchErrors(); }, [fetchErrors]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await fetch(`${backendUrl}api/app-errors/?before_days=${days || 30}`, {
        method: 'DELETE', headers: authHeader,
      });
      setClearDlg(false);
      setPage(1);
      fetchErrors();
    } finally {
      setClearing(false);
    }
  };

  const stats = data?.stats;
  const pagination = data?.pagination;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1300, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <BugReportIcon sx={{ color: AMBER, fontSize: 28 }} />
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#f1f5f9', fontFamily: '"Outfit", sans-serif' }}>
          App Error Log
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <IconButton onClick={fetchErrors} sx={{ color: '#64748b', '&:hover': { color: AMBER } }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          startIcon={<DeleteSweepIcon />}
          onClick={() => setClearDlg(true)}
          size="small"
          sx={{ color: '#f87171', borderColor: 'rgba(248,113,113,0.3)', borderWidth: 1, borderStyle: 'solid', borderRadius: 2, px: 1.5 }}
        >
          Clear old
        </Button>
      </Box>

      {/* Stat cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="Total" value={stats?.total} />
        <StatCard label="JS Errors" value={stats?.by_type?.js_error ?? 0} color="#f87171" />
        <StatCard label="Promise" value={stats?.by_type?.promise ?? 0} color="#fb923c" />
        <StatCard label="Render" value={stats?.by_type?.render ?? 0} color="#c084fc" />
        <StatCard label="Users affected" value={stats?.affected_users} color="#34d399" />
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, background: SURFACE, border: `1px solid ${BORDER}`, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ color: '#64748b' }}>Period</InputLabel>
          <Select
            value={days}
            label="Period"
            onChange={e => { setDays(e.target.value); setPage(1); }}
            sx={{ color: '#e2e8f0', '.MuiOutlinedInput-notchedOutline': { borderColor: BORDER }, '.MuiSvgIcon-root': { color: '#64748b' } }}
          >
            <MenuItem value="1">Last 24 h</MenuItem>
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="">All time</MenuItem>
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={errorType}
          exclusive
          size="small"
          onChange={(_, v) => { setErrorType(v ?? ''); setPage(1); }}
          sx={{ '& .MuiToggleButton-root': { color: '#64748b', borderColor: BORDER, px: 1.5, py: 0.5, fontSize: '0.78rem' },
               '& .Mui-selected': { background: 'rgba(245,158,11,0.12) !important', color: `${AMBER} !important` } }}
        >
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="js_error">JS</ToggleButton>
          <ToggleButton value="promise">Promise</ToggleButton>
          <ToggleButton value="render">Render</ToggleButton>
        </ToggleButtonGroup>

        <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 1, flex: 1, minWidth: 200 }}>
          <TextField
            size="small"
            placeholder="Search message or user…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiInputBase-root': { color: '#e2e8f0', background: 'rgba(0,0,0,0.2)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
              '& input::placeholder': { color: '#334155' },
            }}
          />
          <Button type="submit" variant="contained" size="small"
            sx={{ background: AMBER, color: NAV_BG, fontWeight: 700, '&:hover': { background: AMBER_DARK } }}>
            Search
          </Button>
          {search && (
            <Button size="small" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
              sx={{ color: '#64748b' }}>
              Clear
            </Button>
          )}
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, background: SURFACE, border: `1px solid ${BORDER}` }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { borderBottom: `1px solid ${BORDER}`, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 } }}>
              <TableCell />
              <TableCell>Time</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Message</TableCell>
              <TableCell><PersonIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.3 }} />User</TableCell>
              <TableCell><PhoneAndroidIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.3 }} />OS</TableCell>
              <TableCell>Version</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, borderBottom: 'none' }}>
                  <CircularProgress size={28} sx={{ color: AMBER }} />
                </TableCell>
              </TableRow>
            ) : !data?.errors?.length ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, borderBottom: 'none', color: '#334155' }}>
                  <WarningAmberIcon sx={{ fontSize: 32, mb: 1, display: 'block', mx: 'auto', opacity: 0.3 }} />
                  No errors found
                </TableCell>
              </TableRow>
            ) : (
              data.errors.map(err => <ErrorRow key={err.error_id} err={err} />)
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 2 }}>
          <IconButton disabled={page <= 1} onClick={() => setPage(p => p - 1)} sx={{ color: page <= 1 ? '#1e293b' : '#94a3b8' }}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography sx={{ color: '#64748b', fontSize: '0.85rem' }}>
            Page {page} of {pagination.pages}
          </Typography>
          <IconButton disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)} sx={{ color: page >= pagination.pages ? '#1e293b' : '#94a3b8' }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}

      {/* Clear dialog */}
      <Dialog open={clearDlg} onClose={() => setClearDlg(false)}
        PaperProps={{ sx: { background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 3, minWidth: 320 } }}>
        <DialogTitle sx={{ color: '#f1f5f9', fontFamily: '"Outfit", sans-serif' }}>Clear old errors</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
            Delete all errors older than <strong style={{ color: '#e2e8f0' }}>{days || 30} days</strong>?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setClearDlg(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleClear} disabled={clearing}
            sx={{ background: '#ef4444', '&:hover': { background: '#dc2626' }, fontWeight: 700 }}>
            {clearing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
